/**
 * The pure view-model behind the profile page. No React, no fetching — the
 * route hands it the four things it has already read and renders what comes
 * back.
 *
 * A profile has two halves and either may be missing:
 *
 * - the **account**, when someone is signed in: who they are, the name they
 *   play under, and every venue that account has actually sat down in;
 * - the **membership**, when this phone holds one for the casino in the URL:
 *   the chips on the rail here and what they did with them.
 *
 * A signed-in account also gets an invite card: the link that brings other
 * owners in, and what it has brought so far.
 *
 * A guest phone has only the second. A signed-in owner who has not played
 * tonight has only the first. Both empty is still a page — it says how to get
 * one — which is why nothing here returns null.
 */
import {
  PROFILE_COLOR_IDS,
  PROFILE_COLORS,
  deriveStats,
  profileHex,
  profileInitials,
  type CasinoMemberView,
  type MemberStatsView,
  type ProfileCasinoPlay,
  type ProfileColorId,
  type ProfilePlayResponse,
  type ReferralSummary,
} from '@tv-casino/shared';
import { formatSignedChips as signed } from '../lib/format.js';
import type { CasinoLookup } from '@tv-casino/shared';
import type { SessionUser } from '@tv-casino/shared';

const chips = (n: number): string => n.toLocaleString();

const percent = (rate: number | null): string =>
  rate === null ? '—' : `${(rate * 100).toFixed(1)}%`;

export interface StatLine {
  label: string;
  value: string;
}

/** One swatch in the colour picker. */
export interface ProfileColorOption {
  id: ProfileColorId;
  label: string;
  hex: string;
  active: boolean;
}

/** One venue in the account's play history. */
export interface ProfileVenueRow {
  casinoId: string;
  joinCode: string;
  name: string;
  /** The chips still on the rail there. */
  chips: string;
  /** Lifetime result at this venue, signed. */
  net: string;
  /** True when `net` is negative — the row prints it in the losing colour. */
  down: boolean;
  rounds: string;
  owned: boolean;
  /** Where the "open" link goes: the console for a venue they own, else play. */
  href: string;
}

export interface ProfileAccountVM {
  displayName: string;
  email: string;
  /** The name they play under, or the prompt to pick one. */
  playerName: string | null;
  playerNameLine: string;
  venues: ProfileVenueRow[];
  /** Cross-venue totals, already folded. Empty when they have never played. */
  lifetime: StatLine[];
  /** Said instead of the venue list when it is empty. */
  emptyVenuesLine: string | null;
}

export interface ProfileMembershipVM {
  casinoId: string;
  memberId: string;
  /** The venue's name when the lookup resolved, else its join code. */
  venueName: string;
  joinCode: string;
  name: string;
  chips: string;
  /** Chips minus what the house staked: the up/down line. */
  swing: string;
  down: boolean;
  /** What the house has staked this seat, for the line under the swing. */
  buyIn: string;
  /** Per-game detail — the same lines the owner's drill-down prints. */
  lines: StatLine[];
  emptyLine: string | null;
}

/** The account's referral link and its three counts. */
export interface ProfileInviteVM {
  code: string;
  /** The landing page with the code on it: any page catches `?ref=`. */
  link: string;
  stats: StatLine[];
  /** What the impressions count means, printed under the numbers. */
  note: string;
}

export interface ProfileVM {
  account: ProfileAccountVM | null;
  membership: ProfileMembershipVM | null;
  /** Null signed out, or while the counts have not been read. */
  invite: ProfileInviteVM | null;
  /** The hue every accent on the page is painted in. */
  accentHex: string;
  /** Up to two letters for the avatar disc. */
  initials: string;
  /** The name under the avatar: the account's, else the membership's. */
  headline: string;
  /** One line saying which of the two halves the page is showing. */
  subhead: string;
  colors: ProfileColorOption[];
  /**
   * The colour a save would write. The account's when signed in — it is the
   * identity that outlives any one casino — else the membership's.
   */
  color: ProfileColorId | null;
  /** The name a save would write, prefilled into the form. */
  editableName: string;
  /** False when there is nothing to edit: no account and no membership. */
  editable: boolean;
  /** Shown in place of both halves when the page has nothing at all. */
  emptyLine: string | null;
}

/** The cross-venue summary. Rates are omitted rather than shown as a zero. */
export function totalsLines(totals: MemberStatsView): StatLine[] {
  if (totals.rounds === 0) return [];
  const d = deriveStats(totals);
  return [
    { label: 'Rounds', value: chips(totals.rounds) },
    { label: 'Wagered', value: chips(totals.wagered) },
    { label: 'Net', value: signed(totals.net) },
    { label: 'Win rate', value: percent(d.winRate) },
    { label: 'ROI', value: percent(d.roi) },
    { label: 'Avg bet', value: d.avgBet === null ? '—' : chips(Math.round(d.avgBet)) },
    { label: 'Best round', value: signed(totals.bestRound) },
    { label: 'Worst round', value: signed(totals.worstRound) },
    { label: 'Best streak', value: `${totals.bestWinStreak} won in a row` },
  ];
}

const venueRow = (play: ProfileCasinoPlay): ProfileVenueRow => ({
  casinoId: play.casinoId,
  joinCode: play.joinCode,
  name: play.casinoName,
  chips: chips(play.chips),
  net: signed(play.totals.net),
  down: play.totals.net < 0,
  rounds: play.totals.rounds === 1 ? '1 round' : `${chips(play.totals.rounds)} rounds`,
  owned: play.owned,
  href: play.owned ? `/casino?casino=${play.joinCode}` : `/play?casino=${play.joinCode}`,
});

function accountOf(session: SessionUser, play: ProfilePlayResponse | null): ProfileAccountVM {
  const venues = (play?.casinos ?? []).map(venueRow);
  return {
    displayName: session.name,
    email: session.email,
    playerName: session.playerName,
    playerNameLine: session.playerName
      ? `Plays as ${session.playerName}`
      : 'No play name yet — pick one and it is yours at every casino you own.',
    venues,
    lifetime: play ? totalsLines(play.totals) : [],
    emptyVenuesLine:
      venues.length === 0 ? 'No venues played yet. Sit down at a table and this fills in.' : null,
  };
}

function inviteOf(referrals: ReferralSummary, base: string): ProfileInviteVM {
  return {
    code: referrals.code,
    link: `${base.replace(/\/+$/, '')}/?ref=${referrals.code}`,
    stats: [
      { label: 'Impressions', value: chips(referrals.impressions) },
      { label: 'Sign-ups', value: chips(referrals.signups) },
      { label: 'Owners', value: chips(referrals.owners) },
    ],
    note:
      'Impressions are people who joined a casino you run. A signed-in player counts once; a guest phone counts once per casino. Owners are sign-ups who opened a casino of their own.',
  };
}

function membershipOf(
  member: CasinoMemberView,
  casino: CasinoLookup | null,
  casinoCode: string | null,
  statLines: StatLine[],
): ProfileMembershipVM {
  const swing = member.chips - member.buyIn;
  return {
    casinoId: member.casinoId,
    memberId: member.memberId,
    venueName: casino?.name || casinoCode || 'This casino',
    // A private venue's lookup redacts the code, and the link back to the
    // floor is not a code to print — it is the one this phone already used
    // to get here, so it survives the redaction.
    joinCode: casino?.joinCode || casinoCode || '',
    name: member.name,
    chips: chips(member.chips),
    swing: signed(swing),
    down: swing < 0,
    buyIn: chips(member.buyIn),
    lines: statLines,
    emptyLine:
      member.totals.rounds === 0 ? 'No rounds played here yet — the stats fill in as you play.' : null,
  };
}

export interface ProfileInput {
  session: SessionUser | null;
  play: ProfilePlayResponse | null;
  /** The account's referral counts, when signed in and read. */
  referrals?: ReferralSummary | null;
  /** This page's origin: the invite link's host when nothing better is known. */
  origin?: string;
  /**
   * The server's configured public URL. It wins over the page's origin: an
   * owner reading their profile on a LAN address would otherwise hand a
   * friend across town a link to `192.168.x.x`.
   */
  publicUrl?: string | null;
  member: CasinoMemberView | null;
  casino: CasinoLookup | null;
  /**
   * The venue code this page was opened under — the URL's, else the last one
   * this phone sat down in. Kept separate from the lookup because a private
   * venue's lookup answers with no code at all.
   */
  casinoCode?: string | null;
  /**
   * The membership's per-game lines. Passed in rather than derived here so
   * the page keeps using the one `statLinesOf` the owner console already
   * prints, instead of growing a second spelling of the same stats.
   */
  memberLines: StatLine[];
}

export function profileModel({
  session,
  play,
  referrals = null,
  origin = '',
  publicUrl = null,
  member,
  casino,
  casinoCode = null,
  memberLines,
}: ProfileInput): ProfileVM {
  const account = session ? accountOf(session, play) : null;
  const membership = member ? membershipOf(member, casino, casinoCode, memberLines) : null;

  // The account's hue wins: it is the identity that outlives any one casino,
  // and a save writes it to both halves, so they only differ until the next one.
  const color = session ? session.profileColor : (member?.color ?? null);
  const headline = session?.playerName || session?.name || member?.name || 'Your profile';

  const subhead = account
    ? membership
      ? `Signed in, and playing at ${membership.venueName}.`
      : 'Signed in. No casino open on this phone.'
    : membership
      ? `Playing at ${membership.venueName} on this phone.`
      : 'Not signed in, and no membership on this phone.';

  return {
    account,
    membership,
    invite: session && referrals ? inviteOf(referrals, publicUrl || origin) : null,
    accentHex: profileHex(color),
    initials: profileInitials(headline),
    headline,
    subhead,
    colors: PROFILE_COLOR_IDS.map((id) => ({
      id,
      label: PROFILE_COLORS[id].label,
      hex: PROFILE_COLORS[id].hex,
      active: id === color,
    })),
    color,
    editableName: session?.playerName ?? member?.name ?? '',
    editable: Boolean(session || member),
    emptyLine:
      account || membership
        ? null
        : 'Sign in to keep a profile, or scan a casino’s code to open a membership.',
  };
}
